import { Alert, Button, Grid, Snackbar, Stack, Typography, Box, Chip } from "@mui/material";
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MainCard from "../../components/cards/MainCard";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import SimulationForm from "./SimulationForm";
import {
    useCreateSimulationMutation,
    useGetSimulationByIdQuery,
    useUpdateSimulationMutation
} from "../../store/services/simulation-service";
import { useState } from "react";
import { useSelector } from "react-redux";
import SimulationSkeletonComponent from "../../components/Skeleton/SimulationSkeletonComponent";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { getStatusMessage } from "../../utils/getStatusMessage";

export default function SimulationDetails() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    // Get copy data from navigation state
    const copyFromId = searchParams.get('copyFrom');
    const isCopyMode = Boolean(copyFromId);

    // Use tenancyId from Redux instead of user.tenancyId so sims are created against the toggled tenancy
    const { tenancyId } = useSelector((state) => state.tenancy);

    const [createSimulation] = useCreateSimulationMutation();
    const [updateSimulation] = useUpdateSimulationMutation();

    const { data: simulationData, isLoading, error: isError } = useGetSimulationByIdQuery({ simId: isEditMode ? id : copyFromId, includes: "" }, { skip: !isEditMode && !isCopyMode });

    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // Determine initial data based on mode
    const getInitialData = () => {
        if (isEditMode) {
            return simulationData;
        } else if (isCopyMode) {
            // Add null check to prevent destructuring error
            if (!simulationData) {
                return null; // or return default values
            }

            const user = JSON.parse(localStorage.getItem("user"));
            const isAdmin = user?.role === "Administrators";

            // Exclude problematic fields, keep everything else
            const {
                id,
                randomSend,
                useSimsUrl,
                createdAt,
                updatedAt,
                createdBy,
                emailSimulationId,
                // Override certain fields for copies
                published: _published,
                sharedTenancyIds: _sharedTenancyIds,
                publishToFamily: _publishToFamily,
                ...copiedFields
            } = simulationData;

            return {
                ...copiedFields,
                // Set copy-specific defaults
                simulationName: `${simulationData.simulationName} - Copy`,
                published: true,
                // Fall back to the login tenancy if the toggle is unset; Number("") is 0, which means "shared globally"
                sharedTenancyIds: isAdmin ? [] : [Number(tenancyId) || user?.tenancyId],
                publishToFamily: false
            };
        } else {
            // Base create case
            const user = JSON.parse(localStorage.getItem("user"));
            const isAdmin = user?.role === "Administrators";

            return {
                simulationName: "",
                emailSubject: "",
                emailDisplayName: "",
                emailFromAddress: "",
                simulationType: "Other",
                simsDomainName: "",
                difficultyRating: 2,
                tags: [],
                content: "",
                linkedUniversityCourseId: "Phishing 101",
                published: true,
                // Fall back to the login tenancy if the toggle is unset; Number("") is 0, which means "shared globally"
                sharedTenancyIds: isAdmin ? [] : [Number(tenancyId) || user?.tenancyId],
                publishToFamily: false
            };
        }
    };

    // Show loading skeleton for edit/copy modes when data is loading
    if (isLoading && (isEditMode || isCopyMode)) {
        return <SimulationSkeletonComponent />;
    }

    // Show error only for edit mode when there's an actual error
    if (!isLoading && isError && (isEditMode || isCopyMode)) {
        return (
            <MainCard boxShadow={true} border={false} sx={{ minHeight: "calc(100vh - 200px)", display: "flex", justifyContent: "center", alignItems: "center", bgcolor: 'background.default' }}>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Stack direction="column" spacing={4} alignItems="center">
                            <ErrorOutlineIcon color="error" style={{ fontSize: 80 }} />
                            <Typography variant="h3" align="center" color="textPrimary">
                                {getStatusMessage(isError)}
                            </Typography>
                            <Typography variant="body1" align="center" color="textSecondary">
                                Please refresh the page or contact the administrator.
                            </Typography>
                            <Button onClick={() => navigate(-1)} variant="contained" startIcon={<KeyboardArrowLeftTwoToneIcon />}>
                                Back
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }

    const initialData = getInitialData();

    // If we're in copy mode and data isn't ready yet, show loading
    if (isCopyMode && !initialData) {
        return <SimulationSkeletonComponent />;
    }

    const handleSaveSimulation = async (simsData) => {
        if (isEditMode) {
            simsData.emailSimulationId = id;


            try {
                const response = await updateSimulation({ simData: simsData });
                if (response.data && response.data.message === "Ok") {
                    setSnackbarMessage("Simulation updated successfully");
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);
                } else {
                    setSnackbarMessage("Error occurred while updating simulation");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }
            } catch (e) {
                setSnackbarMessage("Error occurred while updating simulation");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } else {
            // Handle both create and copy modes
            try {
                console.log(simsData, "isCopyMode:", isCopyMode);
                simsData.name = simsData.simulationName;
                const response = await createSimulation(simsData);
                if (response.data && response.data.message === "Ok") {
                    const originalName = simulationData?.simulationName || "original simulation";
                    const message = isCopyMode
                        ? `Simulation copied successfully from "${originalName}"`
                        : "Simulation created successfully";
                    setSnackbarMessage(message);
                    setSnackbarSeverity("success");
                    setSnackbarOpen(true);

                    // Optionally navigate to the simulations list after successful creation
                    setTimeout(() => {
                        navigate("/tenancy-simulations");
                    }, 2000);
                } else {
                    const message = isCopyMode
                        ? "Error occurred while copying simulation"
                        : "Error occurred while creating simulation";
                    setSnackbarMessage(message);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                }
            } catch (e) {
                const message = isCopyMode
                    ? "Error occurred while copying simulation"
                    : "Error occurred while creating simulation";
                setSnackbarMessage(message);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        }
    };

    return (
        <>
            <Grid container rowSpacing={4.5} columnSpacing={2.75}>
                <Grid item xs={12}>
                    <Grid container justifyContent="space-between">
                        <Button onClick={() => navigate(-1)} variant="contained" startIcon={<KeyboardArrowLeftTwoToneIcon />}>
                            Back
                        </Button>
                    </Grid>
                    <MainCard sx={{ mt: 2, bgcolor: 'background.default' }} content={true} boxShadow={true} border={false}>
                        <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="h2">
                                    {isEditMode ? initialData?.simulationName || 'View Simulation' : "Create Simulation"}
                                </Typography>

                            </Box>
                        </Grid>

                        {/* Show informational message for copy mode */}
                        {isCopyMode && simulationData && (
                            <Alert severity="info" sx={{ mb: 3 }}>
                                <Typography variant="body2">
                                    You are creating a copy from "{simulationData.simulationName}". You can modify any details below before saving.
                                </Typography>
                            </Alert>
                        )}

                        <SimulationForm
                            initialData={initialData}
                            onSave={handleSaveSimulation}
                            mode={isEditMode ? "view" : "add"}
                        />
                    </MainCard>
                </Grid>
            </Grid>
            <Snackbar anchorOrigin={{ vertical: "top", horizontal: "center" }} open={snackbarOpen} autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}>
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
};